import React, { ButtonHTMLAttributes } from "react";
import { VideoCameraIcon } from "@heroicons/react/24/solid";
import ToolboxIcon from "../assets/icons/Toolbox.svg";
import getClassName from "./utils/combineClassName";
import Button from "./Button";

interface PsychButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: React.ReactNode;
  favButton?: React.ReactNode;
  text?: string;
  subtitle?: string;
  variant?: "wide" | "tall";
  className?: string;
  isDisabled?: boolean;
  fadeSubtitle?: boolean;
  // New props for toolbar layout
  onOpen?: (e: React.MouseEvent) => void;
  onFavorite?: React.ReactNode;
  onTrailer?: (e: React.MouseEvent) => void;
  onToolbox?: (e: React.MouseEvent) => void;
  hasTrailer?: boolean;
  hasToolbox?: boolean;
  useToolbarLayout?: boolean;
}

function PsychButton({
  favButton,
  variant = "wide",
  icon,
  text,
  subtitle,
  className,
  isDisabled,
  fadeSubtitle,
  onOpen,
  onFavorite,
  onTrailer,
  onToolbox,
  hasTrailer = false,
  hasToolbox = false,
  useToolbarLayout = false,
  ...props
}: PsychButtonProps) {
  // New toolbar layout
  if (useToolbarLayout) {
    return (
      <div
        className={getClassName([
          "flex flex-col rounded-lg bg-gray-100 shadow-md overflow-hidden",
          isDisabled ? "opacity-80 grayscale" : "",
          className,
        ])}
      >
        {/* Top section: Icon + Title/Subtitle */}
        <div className="flex flex-row items-center p-3 pb-2">
          {icon && (
            <div className="w-14 h-14 flex justify-center items-center mr-3 flex-shrink-0">
              {icon}
            </div>
          )}
          <div className="flex-grow text-left">
            {text && <div className="text-lg font-bold">{text}</div>}
            {subtitle && (
              <div
                className={`text-sm ${fadeSubtitle ? "text-purple-500" : "text-electrum"}`}
              >
                {subtitle}
              </div>
            )}
          </div>
        </div>

        {/* Bottom toolbar: Icon buttons + Open button */}
        <div className="flex flex-row items-center justify-between px-3 pb-3 pt-1 border-t border-electrum/20">
          {/* Icon buttons on the left */}
          <div className="flex flex-row items-center gap-2">
            {/* Favorite button */}
            {onFavorite && (
              <div className="flex items-center justify-center">
                {onFavorite}
              </div>
            )}

            {/* Trailer button */}
            {hasTrailer && onTrailer && (
              <Button.Circle
                variant="secondary"
                onClick={onTrailer}
                className="p-2 focus:outline-none focus:ring-0"
                aria-label={text ? `${text} preview` : "Preview"}
                title={text ? `${text} preview` : "Preview"}
                disabled={isDisabled}
              >
                <VideoCameraIcon className="w-5 h-5 text-electrum-300" />
              </Button.Circle>
            )}

            {/* Toolbox button */}
            {hasToolbox && onToolbox && (
              <Button.Circle
                variant="secondary"
                onClick={onToolbox}
                className="p-2 focus:outline-none focus:ring-0"
                aria-label={text ? `Go to ${text} Toolbox` : "Open toolbox"}
                title={text ? `Go to ${text} Toolbox` : "Open toolbox"}
                disabled={isDisabled}
              >
                <img src={ToolboxIcon} alt="" className="w-5 h-5" />
              </Button.Circle>
            )}
          </div>

          {/* Open button on the right */}
          <Button
            variant="primary"
            size="sm"
            onClick={onOpen}
            disabled={isDisabled}
            className="px-5"
          >
            <span className="font-bold">Open</span>
          </Button>
        </div>
      </div>
    );
  }

  // Original layout (keeping for backward compatibility)
  const wideStyles = "flex flex-row h-[96px] min-h-[96px]";
  const tallStyles = "flex flex-col w-[112px] h-fit";

  const variantStyles = variant === "tall" ? tallStyles : wideStyles;

  return (
    <div className={getClassName([variantStyles, className])}>
      <button
        {...props}
        className={getClassName([
          "p4l-button flex flex-grow",
          variant === "tall"
            ? "rounded-t-lg flex flex-col items-center"
            : "rounded-l-lg items-center",
          isDisabled
            ? "cursor-not-allowed bg-gray opacity-80 grayscale"
            : "bg-white text-black border-2 border-electrum hover:border-electrum",
        ])}
      >
        {icon && (
          <div className="w-16 h-16 flex justify-center items-center mr-2 -ml-4 flex-shrink-0">
            {icon}
          </div>
        )}
        <div className="flex-grow text-left">
          {text && <div className="text-xl">{text}</div>}
          {subtitle && (
            <div
              className={`text-sm ${fadeSubtitle ? "text-purple-500" : "text-electrum"}`}
            >
              {subtitle}
            </div>
          )}
        </div>
      </button>
      <div
        className={getClassName([
          "border-2 border-electrum items-center justify-center flex",
          variant === "tall"
            ? "rounded-b-lg h-10 border-t-0"
            : "rounded-r-lg w-12 border-l-0 flex-shrink-0",
          isDisabled
            ? "cursor-not-allowed border-gray-400"
            : "bg-white text-black hover:border-electrum",
        ])}
      >
        {favButton && (
          <span className={`${isDisabled ? "hidden cursor-not-allowed" : ""}`}>
            {favButton}
          </span>
        )}
      </div>
    </div>
  );
}

export default PsychButton;
